import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "@/Store";
import Api from "@/services/Api";
import { buildListConfigs, type ListKind } from "@/utils/statistics";
import { useResolvePotentiallyLocalizedString } from "./useResolvePotentiallyLocalizedString";
import type { DetailedDatum } from "@/declarations/models/Plausible";

type Range = string | string[];

interface CommonArgs {
  dateRange: Range;
  dimensions: string | null;
  selectedPage: string | null;
  selectedQr: string | null;
  selectedLanguage: string | null;
  documentId: string | null;
  plausibleProperty: string | null;
  pageUrl: string;
  enabled?: boolean;
}

function mapCommon(args: CommonArgs & { metrics?: string[]; selectedMetric?: string[] }) {
  return {
    dimensions: args.dimensions,
    metrics: args.metrics ?? args.selectedMetric,
    document_id: args.documentId,
    page_url: args.pageUrl,
    plausible_property: args.plausibleProperty
  };
}

function useAsyncResource<TResponse, TRequestBody extends object, TResult = TResponse>(
  makeRequest: (body: TRequestBody) => { fetchDirect: (o: object) => Promise<TResponse>; abort: () => void },
  body: TRequestBody | null,
  transform: (data: TResponse) => TResult = (d) => d as unknown as TResult,
) {
  const [data, setData] = useState<TResult | null>(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (!body) return;
    const req = makeRequest(body);
    let cancelled = false;
    setLoading(true);
    req.fetchDirect({})
      .then((d) => { if (!cancelled) setData(transform(d)); })
      .catch((e) => { if (!cancelled) console.error(e); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; req.abort(); };
  }, [JSON.stringify(body)]);

  return { data, loading };
}

export function usePlausibleSummary(args: CommonArgs & { metrics: string[] }) {
  const ownerId = useStore().state?.cmsContextInstance?.owner_id;
  return useAsyncResource(
    Api.getPlausibleStatistics,
    (args.enabled ?? true) && ownerId && args.pageUrl ? {
      owner_id: ownerId,
      ...mapCommon(args),
      metrics: args.metrics,
      date_range: args.dateRange,
      selected_page: args.selectedPage,
      selected_qr: args.selectedQr,
      selected_language: args.selectedLanguage,
    } : null,
    (d: any) => d?.results?.[0] ?? null,
  );
}

export function usePlausibleDetailed(args: CommonArgs & { selectedMetric: string[] }) {
  const ownerId = useStore().state?.cmsContextInstance?.owner_id;
  return useAsyncResource(
    Api.getPlausibleDetailedStatistics,
    ownerId && args.pageUrl ? {
      owner_id: ownerId,
      selected_metric: args.selectedMetric,
      date_range: args.dateRange,
      selected_page: args.selectedPage,
      selected_qr: args.selectedQr,
      selected_language: args.selectedLanguage,
      ...mapCommon(args),
    } : null,
    (data: any): DetailedDatum[] =>
      data?.meta?.time_labels?.map((label: string) => {
        const metric =
          data.results?.find((res: any) => res.dimensions?.[0] === label)?.metrics?.[0] ?? "0";
        return { dimension: label, metric, label };
      }) ?? [],
  );
}



export function usePlausibleList(
  kind: ListKind,
  args: CommonArgs & { offset: number; filters?: unknown[]; enabled?: boolean },
) {
  const { t, i18n } = useTranslation("common");
  const resolveLocalized = useResolvePotentiallyLocalizedString(i18n.language);
  const ownerId = useStore().state?.cmsContextInstance?.owner_id;
  const cfg = buildListConfigs(t, resolveLocalized)[kind];
  const filters = args.filters ?? cfg.defaultFilters ?? null;

  return useAsyncResource(
    Api.getPlausibleList,
    (args.enabled ?? true) && ownerId && args.pageUrl
      ? {
          owner_id: ownerId,
          document_id: args.documentId,
          page_url: args.pageUrl,
          plausible_property: args.plausibleProperty,
          dimensions: cfg.dimensions,
          limit: 10,
          offset: args.offset,
          metrics: ["visitors"],
          date_range: args.dateRange,
          ...(filters ? { filters } : {}),
        }
      : null,
    (data: any) => ({
      ...data,
      results: (data?.results ?? []).map((row: any) => cfg.mapRow(row, { t, resolveLocalized })),
    }),
  );
}